import util from '../../../../../utils/miniUtils';
import { toast } from "react-toastify";
import Compressor from "compressorjs";

/**
 * Get catalog data from API (categories, brands, models, sizes)
 * @returns {Promise<Object>} Object containing categories, brands, models, sizes
 */
export const getCatalogData = async () => {
    const request = await util.axios.get( `catalog/data/getSelectorsData` );
    const { error, msg, categories, brands, models, sizes } = request.data;
    if ( error ) throw msg;
    return { categories, brands, models, sizes };
};

/**
 * Get active locations from API
 * @returns {Promise<Array>} List of locations
 */
export const getLocations = async () => {
    const request = await util.axios.get( `locations/data/list` );
    const { error, msg, data } = request.data;
    if ( error ) throw ( msg || 'Failed to load locations' );
    return data || [];
};

/**
 * Validate image sizes (max 10MB each)
 * @param {FileList} files - Files to validate
 * @returns {Object} Validation result with error status and image names
 */
export const validateImageSizes = ( files ) => {
    let imageError = { error: false, imageNames: [] };
    Array.from( files ).forEach( ( file ) => {
        const fileSize = ( file.size / 1024 / 1024 );
        if ( fileSize > 10 ) {
            imageError.error = true;
            imageError.imageNames.push( file.name );
        }
    } );
    
    if ( imageError.error ) {
        const images = imageError.imageNames.join( ", " );
        const message = imageError.imageNames.length > 1 
            ? `${images} exceden el tamaño permitido de 2MB` 
            : `${images} excede el tamaño permitido de 2MB`;
        toast.error( message );
    }
    
    return imageError;
};

/**
 * Compress images using Compressor.js
 * @param {Array} images - Array of image files to compress
 * @returns {Promise<Array>} Array of compressed images
 */
export const compressImages = async ( images ) => {
    const compressedImages = await Promise.all(
        Array.from( images ).map( async ( file ) => {
            return new Promise( ( resolve ) => {
                new Compressor( file, {
                    quality: 0.65,
                    success: ( compressedResult ) => {
                        resolve( compressedResult );
                    },
                } );
            } );
        } )
    );
    return compressedImages;
};

/**
 * Create FormData for product submission
 * @param {Object} product - Product data
 * @param {Array} compressedImages - Compressed image files
 * @param {boolean} moreFields - Whether to include moreFields
 * @returns {FormData} Formatted FormData for API submission
 */
export const createProductFormData = ( product, compressedImages, moreFields ) => {
    const productTemp = { ...product };
    if ( moreFields ) {
        productTemp.moreFields = moreFields;
    }
    delete productTemp.images;
    
    const data = new FormData();
    data.append( 'information', JSON.stringify( productTemp ) );
    
    compressedImages.forEach( image => {
        data.append( 'articleImages', image );
    } );
    
    return data;
};

/**
 * Create new catalog item (category, brand, model, size)
 * @param {string} value - Value to create
 * @param {string} catalog - Catalog type (CATEGORIES, BRANDS, MODELS, SIZES)
 * @returns {Promise<void>}
 */
export const createCatalogItem = async ( value, catalog ) => {
    const info = { value, catalog };
    const request = await util.axios.post( `/catalog/data/create`, info );
    const { error, msg } = request.data;
    if ( error ) throw msg;
};
